import { firebaseAction } from '../utils'
import ReactGA from 'react-ga'
import {
  orderedSectionsSelector,
  blocksSelector
} from '../selectors/boardSelectors'
import { newSectionHeight } from 'variables'
import { confirm } from './alerts'
import { getVal } from 'react-redux-firebase'

export const createSection = (boardId, number) =>
  firebaseAction(async (update, { getState, getKey }) => {
    const id = getKey()
    const sections = orderedSectionsSelector(getState())
    const forward = {
      [`/sections/${boardId}/${id}`]: {
        board: boardId,
        id,
        height: newSectionHeight,
        number
      }
    }
    // move down other sections to make room
    sections.slice(number).forEach(section => {
      forward[`/sections/${section.board}/${section.id}/number`] =
        section.number + 1
    })
    await update({ forward })
    ReactGA.event(
      {
        category: 'Section',
        action: 'Create Section',
        value: number
      },
      ['app']
    )
  })

export const setSectionHeight = (section, height) =>
  firebaseAction(async update => {
    height = Math.max(height, 2)
    await update({
      forward: { [`/sections/${section.board}/${section.id}/height`]: height }
    })
    ReactGA.event(
      {
        category: 'Section',
        action: 'Resize Section',
        value: height
      },
      ['app']
    )
  })

export const setSectionMinHeight = (boardId, sectionId, height) =>
  firebaseAction((update, { getState }) => {
    const currentHeight = getVal(
      getState().firebase.data,
      `sections/${boardId}/${sectionId}/height`
    )
    height = Math.max(currentHeight, height + 1, 2)
    update({
      forward: { [`/sections/${boardId}/${sectionId}/height`]: height }
    })
  })

export const editSectionColor = (thisSection, color) =>
  firebaseAction(async update => {
    const forward = {
      [`/sections/${thisSection.board}/${thisSection.id}/color`]: color
    }
    const backward = {
      [`/sections/${thisSection.board}/${thisSection.id}/color`]:
        thisSection.color || null
    }
    await update({ forward, backward })
    ReactGA.event(
      {
        category: 'Section',
        action: 'Edit Section Color'
      },
      ['app']
    )
  })

export const moveSection = (thisSection, newNumber) =>
  firebaseAction(async (update, { getState }) => {
    const sections = orderedSectionsSelector(getState())
    const forward = {
      // this section goes to new number
      [`/sections/${thisSection.board}/${thisSection.id}/number`]: newNumber
    }
    sections.forEach((section, i) => {
      if (section.number > thisSection.number && section.number <= newNumber) {
        // sectons after where it was but before where it's going need to move up
        forward[`/sections/${section.board}/${section.id}/number`] =
          section.number - 1
      } else if (
        section.number < thisSection.number &&
        section.number >= newNumber
      ) {
        // sectons before where it was but after where it's going need to move down
        forward[`/sections/${section.board}/${section.id}/number`] =
          section.number + 1
      }
    })
    await update({ forward })
    ReactGA.event(
      {
        category: 'Section',
        action: 'Move Section',
        value: newNumber
      },
      ['app']
    )
  })

export const duplicateSection = section =>
  firebaseAction(async (update, { getState, getKey }) => {
    const state = getState()
    const sectionId = getKey()
    const forward = {
      [`/sections/${section.board}/${sectionId}/`]: {
        id: sectionId,
        board: section.board,
        number: section.number + 1,
        height: section.height,
        color: section.color || null
      }
    }
    // duplicate blocks
    const blocks = blocksSelector(state)
    const newGroups = {}
    Object.values(blocks || {}).forEach(block => {
      if (block.section === section.id) {
        const { id, layout, group, ...blockProps } = block
        const blockId = getKey()

        // check if block was in a group
        if (group) {
          if (!newGroups[group]) {
            // create and store ID for cloned group
            newGroups[group] = getKey()
          }
          // use existing new group ID
          blockProps.group = newGroups[group]
        }

        forward[`/blocks/${section.board}/${blockId}/`] = {
          ...blockProps,
          id: blockId,
          section: sectionId
        }
        forward[`/blockLookup/${blockId}`] = `blocks/${
          section.board
        }/${blockId}`
        if (block.image && block.image.pendingUpload) {
          forward[
            `/pendingUploads/${block.image.pendingUpload}/locations/${blockId}`
          ] = `/blocks/${section.board}/${blockId}/`
        }
      }
    })
    // move down other sections
    const sections = orderedSectionsSelector(state)
    sections.slice(section.number + 1).forEach(section => {
      forward[`/sections/${section.board}/${section.id}/number`] =
        section.number + 1
    })
    await update({ forward })
    ReactGA.event({ category: 'Section', action: 'Duplicate Section' }, ['app'])
  })

export const deleteSection = section =>
  firebaseAction(async (update, { getState, dispatch }) => {
    const action = async () => {
      const state = getState()
      const forward = {
        [`/sections/${section.board}/${section.id}/`]: null
      }
      // delete blocks
      const blocks = blocksSelector(state)
      Object.values(blocks || {}).forEach(block => {
        if (block.section === section.id) {
          forward[`/blocks/${section.board}/${block.id}/`] = null
          forward[`/blockLookup/${block.id}`] = null
        }
      })
      // remove any pending uploads
      Object.values(blocks || {}).forEach(block => {
        if (block.section === section.id) {
          if (block.image && block.image.pendingUpload) {
            forward[
              `/pendingUploads/${block.image.pendingUpload}/locations/${
                block.id
              }`
            ] = null
          }
        }
      })
      // move up other sections
      const sections = orderedSectionsSelector(state)
      sections.slice(section.number + 1).forEach(section => {
        forward[`/sections/${section.board}/${section.id}/number`] =
          section.number - 1
      })
      await update({ forward })
      ReactGA.event({ category: 'Section', action: 'Delete Section' }, ['app'])
    }

    dispatch(confirm('section.confirmDelete', action))
  })
